import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getArticles, getSettings, getImageUrl } from "@/lib/api";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import GradientBackground from "@/components/GradientBackground";
import Footer from "@/components/Footer";

export default function BlogList() {
  const [articles, setArticles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getArticles(true), getSettings()]).then(([arts, sett]) => {
      setArticles(arts);
      setSettings(sett);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="blog-loading">
        <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden flex flex-col" data-testid="blog-list-page">
      {/* Animated GradFlow Background */}
      <GradientBackground />

      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-[#ff0090]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors" data-testid="back-to-home-blog">
            <ArrowLeft className="w-4 h-4" />
            {settings.site_name || "LinkStore"}
          </Link>
        </div>
      </nav>

      <section className="relative isolate py-10 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 flex-grow">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-8 sm:mb-12">
            <h1 className="font-['Outfit'] text-3xl sm:text-5xl md:text-7xl font-semibold tracking-tight leading-none text-white" data-testid="blog-title">
              Blog
            </h1>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-white/60">Neueste Artikel und Updates</p>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <BookOpen className="w-12 h-12 text-[#ff0090] mx-auto mb-4" />
              <p className="text-white/60">Noch keine Artikel vorhanden.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="blog-grid">
              {articles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.id}`}
                  className="group rounded-2xl bg-black/50 backdrop-blur-md border-2 border-[#ff0090]/40 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:bg-black/70 hover:border-[#ff0090] hover:shadow-xl hover:shadow-[#ff0090]/20 animate-fade-in-up"
                  style={{ animationDelay: `${idx * 80}ms`, opacity: 0 }}
                  data-testid={`blog-list-card-${article.id}`}
                >
                  {article.cover_image_url && (
                    <div className="overflow-hidden h-40 sm:h-48">
                      <img
                        src={getImageUrl(article.cover_image_url)}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-4 sm:p-5">
                    {article.tags?.length > 0 && (
                      <div className="flex gap-2 mb-2 flex-wrap">
                        {article.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-[#ff0090]/30 text-[#ff69b4] border border-[#ff0090]/50">{tag}</Badge>
                        ))}
                      </div>
                    )}
                    <h3 className="font-['Outfit'] font-bold text-white text-base sm:text-lg leading-tight">{article.title}</h3>
                    <p className="text-xs sm:text-sm text-white/60 mt-2 line-clamp-3">{article.content}</p>
                    <div className="flex items-center text-[#ff0090] text-sm font-medium mt-4">
                      Weiterlesen <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
